import type { TableColumn } from '../ui/DataTable'
import { escapeHtml, stamp } from '../inventoree/format'

/**
 * Coloanele ecranului "Customers" - `OwnersSupportModel` din
 * `resources/js/app/modules/easeedesk/customers.js`, cu acelasi HTML pe
 * celula, aceleasi titluri si aceeasi sortabilitate.
 *
 * O SINGURA schimbare, peste tot: textul scris de om (nume, email, telefon,
 * numele tipului de licenta) trece prin `escapeHtml`. In portal se lipea brut
 * in HTML. Aceeasi hotarare ca la listele deja mutate.
 *
 * CE NU SE DESENEAZA, SI DE CE
 * ----------------------------
 * Coloana 12 desena "Add license" / "Edit license" pe langa "Disable" /
 * "Enable", iar coloana 13 desena "Edit" pe contact. Primele doua deschideau
 * `settings/licenses/ownerSupportLicenseForm`, a treia
 * `settings/contacts/editContact` - formulare ale modulului SETTINGS, care se
 * muta cu Settings. Aici raman numai cele doua butoane care chiar au unde sa
 * duca. Un buton care nu face nimic e mai rau decat unul care lipseste.
 *
 * Coloana 14 (bifa de selectie multipla) merge cu "Send agent download link"
 * din butonul "Actions" de deasupra listei - `settings/contacts/
 * send_agent_link_by_email`, mutat pe 11 septembrie 2026 la
 * `POST /settings/contacts/agent-link`. Bifele se deseneaza cu aceleasi clase
 * (`all-users`, `select-user`), iar pagina asculta clicul pe ele.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** moment(x).format('D MMM YYYY, HH:mm') */
function dayTime(value: unknown): string {
  const when = stamp(value)
  if (!when) return ''
  const hh = String(when.getHours()).padStart(2, '0')
  const mm = String(when.getMinutes()).padStart(2, '0')
  return `${when.getDate()} ${MONTHS[when.getMonth()]} ${when.getFullYear()}, ${hh}:${mm}`
}

/** moment(x).format('ll') - "Sep 9, 2026". */
function shortDay(value: unknown): string {
  const when = stamp(value)
  if (!when) return ''
  return `${MONTHS[when.getMonth()]} ${when.getDate()}, ${when.getFullYear()}`
}

/** Ziua de azi ca "AAAA-LL-ZZ", cum o facea `new Date().toISOString().slice(0, 10)`. */
function today(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

/**
 * Insigna de stare a licentei, exact ramurile din portal: fara licenta,
 * oprita, activa fara perioada, activa in perioada, expirata.
 */
export function licenseBadge(row: Record<string, unknown>): string {
  const disabled = row.disabled
  if (disabled === null || disabled === undefined) return '<span class="label label-default">no license</span>'
  if (Number(disabled) === 1) return '<span class="label label-danger">disabled</span>'
  if (Number(row.by_date_range) === 0) return '<span class="label label-success">active</span>'

  const start = String(row.start_license ?? '')
  const end = String(row.end_license ?? '')
  if (start < end && end > today()) return '<span class="label label-success">active</span>'
  return '<span class="label label-warning">expired</span>'
}

/** Coloana 12: cele doua butoane de licenta care s-au mutat. */
function licenseActions(row: Record<string, unknown>): string {
  const actions = (row.actions ?? {}) as { add?: number; disabled?: number }
  const owner = escapeHtml(row.ID)

  if (Number(actions.add) === 1) {
    return '<span class="text-muted">no license</span>'
  }
  if (Number(actions.disabled) === 1) {
    return `<button type="button" data-owner="${owner}" data-license="enable" class="btn btn-xs btn-primary enable-owner-license">Enable license</button> `
  }
  return `<button type="button" data-owner="${owner}" data-license="disable" class="btn btn-xs btn-danger disable-owner-license">Disable license</button> `
}

export function customerColumns(): TableColumn[] {
  return [
    {
      index: 0,
      title: '',
      orderable: false,
      visible: true,
      width: 30,
      html: (row) =>
        `<center class="center show_details color-blue pointer font-16" data-id="${escapeHtml(row.ID)}" data-type=""><i class="fas fa-user" aria-hidden="true"></i></center>`,
    },
    {
      index: 1,
      title: 'Firstname',
      orderable: true,
      visible: true,
      html: (row) =>
        Number(row.vip) === 1
          ? `<strong title="VIP"><span class="label label-danger label-bolded"><i class="fa fa-star"></i> VIP</span> ${escapeHtml(row.firstname)}</strong>`
          : `<strong>${escapeHtml(row.firstname)}</strong>`,
    },
    { index: 2, title: 'Lastname', orderable: true, visible: true, html: (row) => `<strong>${escapeHtml(row.lastname)}</strong>` },
    { index: 3, title: 'Email', orderable: true, visible: true, html: (row) => escapeHtml(row.email) },
    { index: 4, title: 'Phone', orderable: true, visible: true, html: (row) => escapeHtml(row.phone) },
    { index: 5, title: 'Mobile Phone', orderable: true, visible: true, html: (row) => escapeHtml(row.mobile_phone) },
    { index: 6, title: 'Created', orderable: true, visible: true, html: (row) => escapeHtml(dayTime(row.create_date)) },
    { index: 7, title: 'Last Edit', orderable: true, visible: true, html: (row) => escapeHtml(dayTime(row.last_edit)) },
    { index: 8, title: 'License status', orderable: true, visible: true, html: licenseBadge },
    {
      index: 9,
      title: 'License start',
      orderable: true,
      visible: true,
      html: (row) => (row.start_license && Number(row.by_date_range) > 0 ? escapeHtml(shortDay(row.start_license)) : ''),
    },
    {
      index: 10,
      title: 'License end',
      orderable: true,
      visible: true,
      html: (row) => (row.end_license && Number(row.by_date_range) > 0 ? escapeHtml(shortDay(row.end_license)) : ''),
    },
    { index: 11, title: 'License type', orderable: true, visible: true, html: (row) => escapeHtml(row.license_type_name ?? '') },
    { index: 12, title: 'License Actions', orderable: false, visible: true, html: licenseActions },
    {
      index: 13,
      title: 'Multiple select',
      titleHtml: '<input type="checkbox" class="all-users"/><span class="hidden">Multiple select</span>',
      orderable: false,
      visible: true,
      className: 'center',
      html: (row) => `<input type="checkbox" data-id="${escapeHtml(row.ID)}" class="select-user"/>`,
    },
  ]
}
